import type { ListBuilderConfiguration } from '../../types';
import { allMerchandisersList } from './lists/all-merchandisers';
import { highTempMerchandisersList } from './lists/high-temp';
import { inactiveMerchandisersList } from './lists/inactive';
import { maintenanceMerchandisersList } from './lists/maintenance';
import { needsRefillMerchandisersList } from './lists/needs-refill';

/**
 * Merchandiser list configuration (PD-13923). Backed by the merchandiser
 * entity; the dedicated UI replaces the legacy location-based merchandiser list.
 *
 * Multi-view list (PD-13938) with 5 tabs — All / Needs Refill / Maintenance /
 * Inactive / High Temp — reborn from the legacy location-list merchandiser
 * tabs. Tabs apply their view via filterRules defaults (the vehicle pattern).
 */
export const MERCHANDISER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		allMerchandisersList,
		needsRefillMerchandisersList,
		maintenanceMerchandisersList,
		inactiveMerchandisersList,
		highTempMerchandisersList,
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'merchandiserSearch',
			'merchandiserStatus',
			'merchandiserPlacement',
			'merchandiserModel',
			'merchandiserFillLevel',
			'merchandiserHealth',
			'merchandiserHighTemp',
			'merchandiserRefillNeeded',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'merchandiser-name',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Sort merchandisers alphabetically by name',
		},
		{
			id: 'merchandiser-name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Sort merchandisers reverse-alphabetically by name',
		},
		{
			id: 'status-asc',
			name: 'Status (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'status', objectTable: 'merchandiser' },
				{ direction: 'ASC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Group merchandisers by status, then by name',
		},
		// PD-16059 — rollup-derived sorts. The backend orders the whole
		// filtered set in memory (nulls last either way) before paginating.
		// Named to match the Fill % / Qty Needed column header sorts exactly,
		// so the dropdown shows one entry per direction and a header click
		// and a dropdown pick resolve to the same option.
		{
			id: 'fill-pct-asc',
			name: 'Fill % (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'fillPercentage',
					objectTable: 'merchandiser',
				},
			],
			description: 'Emptiest merchandisers first; unmeasured units last',
		},
		{
			id: 'fill-pct-desc',
			name: 'Fill % (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'fillPercentage',
					objectTable: 'merchandiser',
				},
			],
			description: 'Fullest merchandisers first; unmeasured units last',
		},
		{
			id: 'quantity-needed',
			name: 'Qty Needed',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'quantityNeeded',
					objectTable: 'merchandiser',
				},
			],
			description: 'Merchandisers short the most units first',
		},
	],
	objects: [
		{
			baseUrl: '/distribution/merchandisers/:id',
			objectLabel: 'Merchandiser',
			objectTable: 'merchandiser',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'model', fieldLabel: 'Model' },
				{ fieldName: 'year', fieldLabel: 'Year' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'locationInfo', fieldLabel: 'Placement' },
				{ fieldName: 'locationsCount', fieldLabel: 'Locations' },
				{ fieldName: 'itemsCount', fieldLabel: 'Items' },
				{ fieldName: 'totalQuantity', fieldLabel: 'On Hand' },
				{ fieldName: 'fillPercentage', fieldLabel: 'Fill %' },
				{ fieldName: 'quantityNeeded', fieldLabel: 'Qty Needed' },
				{ fieldName: 'stockLevel', fieldLabel: 'Stock' },
				{ fieldName: 'healthState', fieldLabel: 'Health' },
				{ fieldName: 'alertSummary', fieldLabel: 'Alerts' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
			],
		},
	],
};
